//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { updateLabels } from '@apis/bulkActionsApi';
import { updateMediaItemTags } from '@apis/universal-image-actions/universalImageActionsApi';
import { IMediaItemTagsRequest } from '@apis/universal-image-actions/universalImageActionsApiModel';
import { manageLabelsQueryKeys } from '@content-drawer/contentDrawerConstants';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import Cuid from '@models/Cuid';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { getNgRedux } from '@react-bridge/redux-bridge';
import { setLastUpdateLabels } from '@uia/uia-modal/modal-slice';

export interface UpdateMediaItemTagsMutationParams {
  cuid: Cuid;
  request: IMediaItemTagsRequest;
  /**
   * Moderator labeling uses the bulk labels endpoint with isModeration=true and
   * add/remove deltas rather than the client per-item tags endpoint.
   */
  isModeration?: boolean;
  labelsToAdd?: string[];
  labelsToRemove?: string[];
}

export const useUpdateMediaItemTagsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      cuid,
      isModeration = false,
      labelsToAdd = [],
      labelsToRemove = [],
      request,
    }: UpdateMediaItemTagsMutationParams) =>
      isModeration
        ? updateLabels(
            [Cuid.toPartial(cuid)],
            { labelsToAdd, labelsToRemove },
            true,
          )
        : updateMediaItemTags(cuid, request),
    onError: () => {
      /**
       * We are unable to use Redux or the Angular injector due to the multiple Angular context issue.
       * TODO: PD-261610 - Migrate the UIA modal to React and remove the shared ngReduxInstance.
       */
      getNgRedux().dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Update media item labels',
          message: `Unable to save media item labels. Please try again or contact Bazaarvoice Support.`,
          type: ToastType.ERROR,
          options: {
            toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
          },
        }),
      );
    },
    onSettled: async (_, __, { cuid, request }) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [
            QueryKeyConstants.UniversalImageActions,
            QueryKeyConstants.UniversalImageActionsLabels,
          ],
        }),
        queryClient.invalidateQueries({
          queryKey: [
            QueryKeyConstants.UniversalImageActions,
            QueryKeyConstants.UniversalImageActionsRights,
            QueryKeyConstants.UniversalImageActionsMetadata,
            cuid,
          ],
        }),
        queryClient.invalidateQueries({
          queryKey: manageLabelsQueryKeys,
        }),
      ]);

      /**
       * We are unable to use Redux or the Angular injector due to the multiple Angular context issue.
       * TODO: PD-261610 - Migrate the UIA modal to React and remove the shared ngReduxInstance.
       */
      getNgRedux().dispatch(setLastUpdateLabels(request.tags));
    },
  });
};
